import { useEffect, useState } from 'react';
import { Logo } from '../components/Logo';
import { AdminSessionBanner } from '../components/AdminSessionBanner';
import { authedFetch } from '../lib/authed-fetch';
import { redirectToPortalLogin, useCurrentUser } from '../lib/use-current-user';
import '../admin.css';

/**
 * Admin home (/admin), Zach 11/2/26 — one organized page for every admin
 * surface on the scheduler, with the journey's live status at the top so
 * nobody has to remember URLs. Read-only; every control lives on the page
 * it links to.
 */

type LaunchPayload = {
  ok: boolean;
  sequences: Array<{ slug: string; status: string; launch: { since?: string; min?: number; max?: number; per_day?: number; open?: boolean } | null }>;
  today: { journey: number; holdout: number; excluded: number };
  ledgerMissing: boolean;
};

type LogPayload = {
  ok: boolean;
  auditTableMissing?: boolean;
  audits: Array<{ audit_id: string; audited_by: string; audited_at: string; verdict: 'ok' | 'issue'; profile?: { name: string | null; email: string } | null }>;
};

type ListPayload = { ok: boolean; prospects: Array<{ profileId: string; isTest: boolean; failed: number; unanswered: number; enrollments: Array<{ status: string }> }>; enrolledLast7d: number };

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86_400_000);
  if (d >= 1) return `${d}d ago`;
  const h = Math.floor(ms / 3_600_000);
  if (h >= 1) return `${h}h ago`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m ago`;
}

const CSS = [
  '.ah-status { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; margin-bottom: 26px; }',
  '.ah-tile { background: #fff; border: 1px solid #dfe3e8; border-left: 5px solid #8C8C8C; border-radius: 8px; padding: 12px 14px; font-size: 13.5px; }',
  '.ah-tile.ok { border-left-color: #1f7a3a; }',
  '.ah-tile.bad { border-left-color: #C8102E; }',
  '.ah-tile.warn { border-left-color: #b4760a; }',
  '.ah-tile .k { font-size: 11px; letter-spacing: 0.6px; text-transform: uppercase; color: #6a6f6a; }',
  '.ah-tile .v { font-size: 20px; font-family: Georgia, serif; color: #0B1F3A; margin: 2px 0; }',
  '.ah-tile .m { font-size: 12px; color: #6a6f6a; }',
  '.ah-tile a { color: #0B1F3A; }',
  '.ah-group { margin-bottom: 26px; }',
  '.ah-group h2 { font-size: 13px; letter-spacing: 0.6px; text-transform: uppercase; color: #6a6f6a; margin: 0 0 10px; }',
  '.ah-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; }',
  '.ah-card { display: block; background: #fff; border: 1px solid #dfe3e8; border-radius: 8px; padding: 14px 16px; text-decoration: none; color: inherit; }',
  '.ah-card:hover { border-color: #0B1F3A; }',
  '.ah-card .t { font-weight: 600; font-size: 15px; color: #0B1F3A; }',
  '.ah-card .t small { font-weight: normal; font-size: 11px; color: #8C8C8C; margin-left: 6px; }',
  '.ah-card .d { font-size: 12.5px; color: #555; margin-top: 4px; line-height: 1.4; }',
  '.ah-card .u { font-size: 11px; color: #8C8C8C; margin-top: 6px; font-family: ui-monospace, Menlo, monospace; }',
].join('\n');

type Link = { href: string; title: string; desc: string; external?: boolean; tag?: string };

const JOURNEY: Link[] = [
  { href: '/admin/journey/prospects', title: 'Prospects console', desc: 'One profile per enrolled prospect: Decision Center link, every email sent and scheduled, meetings and summaries, the message thread, the isolation verdict, and the audit stamp. Run the full audit on demand.', tag: 'start here' },
  { href: '/admin/journey/audit', title: 'Random audit + launch gate', desc: 'Pick real prospects at random and verify them; pause or resume enrollment on the launch gate; the cohort ledger for today.' },
  { href: '/admin/journey', title: 'Raw QC console', desc: 'The engine timeline: every send with its stamped reason, deferrals, held facts and webhook events. For digging, not for daily use.' },
];

const SCHEDULER: Link[] = [
  { href: '/admin/routing', title: 'Routing log', desc: 'Why each booking went to the advisor it did — rule hits, fallbacks and errors.' },
  { href: '/admin/advisors', title: 'Advisors', desc: 'Active, paused and pending advisors; identities, headshots and scheduler bios.' },
  { href: '/admin/availability', title: 'Availability', desc: 'Working hours and overrides per advisor.' },
  { href: '/admin/reminder-opt-outs', title: 'Reminder opt-outs', desc: 'Prospects who unsubscribed from meeting reminders.' },
  { href: '/templates', title: 'Email templates', desc: 'Confirmation and reminder templates.' },
];

const ELSEWHERE: Link[] = [
  { href: 'https://portal.annuityassociation.com/crm', title: 'Command Center', desc: 'The advisor home on the portal — inbox (Decision Center messages), follow-ups, pipeline, conversion.', external: true },
  { href: 'https://portal.annuityassociation.com/command', title: 'Marketing Command Center', desc: 'Paid search, org health and CAC.', external: true },
  { href: 'https://scheduler.annuityassociation.com/dc', title: 'Decision Center (magic link)', desc: 'What a prospect sees when they have lost their email — request a sign-in link.', external: true },
];

export function AdminHome() {
  const auth = useCurrentUser();
  const isAdmin = auth.status === 'authenticated' && auth.user.isAdmin;
  const [gate, setGate] = useState<LaunchPayload | null>(null);
  const [log, setLog] = useState<LogPayload | null>(null);
  const [list, setList] = useState<ListPayload | null>(null);

  useEffect(() => {
    if (auth.status === 'unauthenticated') window.location.href = redirectToPortalLogin();
  }, [auth.status]);

  useEffect(() => {
    if (!isAdmin) return;
    void (async () => {
      try {
        const r = await authedFetch('/api/admin/journey-launch', { cache: 'no-store' });
        const j = (await r.json()) as LaunchPayload;
        if (j.ok) setGate(j);
      } catch {
        /* tile stays grey */
      }
      try {
        const r = await authedFetch('/api/admin/journey-audit?view=log', { cache: 'no-store' });
        const j = (await r.json()) as LogPayload;
        if (j.ok) setLog(j);
      } catch {
        /* tile stays grey */
      }
      try {
        const r = await authedFetch('/api/admin/journey-prospects?view=list', { cache: 'no-store' });
        const j = (await r.json()) as ListPayload;
        if (j.ok) setList(j);
      } catch {
        /* tile stays grey */
      }
    })();
  }, [isAdmin]);

  function renderStatus() {
    const gated = (gate?.sequences ?? []).filter((s) => s.launch);
    const live = gated.filter((s) => s.status === 'live');
    const open = live.filter((s) => s.launch?.open !== false);
    const gateCls = !gate ? '' : live.length === 0 ? 'warn' : open.length === live.length ? 'ok' : 'bad';
    const first = live[0]?.launch ?? null;
    const band = first && (first.min != null || first.max != null) ? `$${((first.min ?? 0) / 1000).toFixed(0)}k–${first.max != null ? `$${(first.max / 1000).toFixed(0)}k` : 'any'}` : null;

    const weekly = (log?.audits ?? []).filter((a) => a.audited_by === 'weekly-auto');
    const lastWeekly = weekly[0] ?? null;
    const lastWeeklyDay = lastWeekly ? lastWeekly.audited_at.slice(0, 10) : null;
    const lastWeeklyRun = lastWeeklyDay ? weekly.filter((a) => a.audited_at.slice(0, 10) === lastWeeklyDay) : [];
    const weeklyIssues = lastWeeklyRun.filter((a) => a.verdict === 'issue').length;
    const lastHuman = (log?.audits ?? []).find((a) => a.audited_by !== 'weekly-auto') ?? null;

    const real = (list?.prospects ?? []).filter((p) => !p.isTest);
    const active = real.filter((p) => p.enrollments.some((e) => e.status === 'active')).length;
    const attention = real.filter((p) => p.failed > 0 || p.unanswered > 0 || p.enrollments.some((e) => e.status === 'paused_reply')).length;

    return (
      <div className="ah-status">
        <div className={'ah-tile ' + gateCls}>
          <div className="k">Journey launch gate</div>
          <div className="v">{!gate ? '…' : live.length === 0 ? 'dark' : open.length === live.length ? 'enrolling' : open.length === 0 ? 'paused' : 'partly paused'}</div>
          <div className="m">
            {gate ? (
              <>
                {live.map((s) => s.slug).join(' + ') || 'no live sequence'}
                {band ? ` · ${band}` : ''}
                {first?.per_day != null ? ` · ${first.per_day}/day` : ''}
                {!gate.ledgerMissing && ` · today ${gate.today.journey} in / ${gate.today.holdout} holdout`}
                {' · '}
                <a href="/admin/journey/audit">manage</a>
              </>
            ) : (
              'loading…'
            )}
          </div>
        </div>
        <div className={'ah-tile ' + (list ? (attention ? 'warn' : 'ok') : '')}>
          <div className="k">Prospects in the journey</div>
          <div className="v">{list ? real.length : '…'}</div>
          <div className="m">
            {list ? (
              <>
                {active} active · {list.enrolledLast7d} enrolled this week{attention ? ` · ${attention} need attention` : ''} ·{' '}
                <a href="/admin/journey/prospects">open</a>
              </>
            ) : (
              'loading…'
            )}
          </div>
        </div>
        <div className={'ah-tile ' + (log ? (lastWeekly ? (weeklyIssues ? 'bad' : 'ok') : 'warn') : '')}>
          <div className="k">Weekly automated audit</div>
          <div className="v">{!log ? '…' : lastWeekly ? (weeklyIssues ? `${weeklyIssues} issue${weeklyIssues === 1 ? '' : 's'}` : 'passed') : 'not run yet'}</div>
          <div className="m">
            {lastWeekly ? `${fmtTs(lastWeekly.audited_at)} (${ago(lastWeekly.audited_at)}) · ${lastWeeklyRun.length} prospects checked` : 'runs every Saturday morning and emails the result'}
            {log?.auditTableMissing ? ' · audit table missing (0097)' : ''}
          </div>
        </div>
        <div className="ah-tile">
          <div className="k">Last human audit</div>
          <div className="v">{!log ? '…' : lastHuman ? (lastHuman.verdict === 'ok' ? 'verified' : 'issue') : 'none yet'}</div>
          <div className="m">{lastHuman ? `${lastHuman.profile?.name ?? lastHuman.profile?.email ?? ''} · ${lastHuman.audited_by} · ${ago(lastHuman.audited_at)}` : 'open a prospect and stamp it'}</div>
        </div>
      </div>
    );
  }

  function group(title: string, links: Link[]) {
    return (
      <section className="ah-group">
        <h2>{title}</h2>
        <div className="ah-grid">
          {links.map((l) => (
            <a key={l.href} className="ah-card" href={l.href} target={l.external ? '_blank' : undefined} rel={l.external ? 'noopener noreferrer' : undefined}>
              <div className="t">
                {l.title}
                {l.tag && <small>{l.tag}</small>}
                {l.external && <small>↗</small>}
              </div>
              <div className="d">{l.desc}</div>
              <div className="u">{l.href.replace('https://', '')}</div>
            </a>
          ))}
        </div>
      </section>
    );
  }

  return (
    <div className="admin-app">
      <style>{CSS}</style>
      <AdminSessionBanner />
      <header className="admin-header">
        <div className="admin-header-inner">
          <Logo className="admin-logo" />
          <div>
            <h1>Admin</h1>
            <p className="admin-sub">Everything on the scheduler that needs an admin, in one place.</p>
          </div>
        </div>
      </header>
      <main className="admin-main">
        {auth.status === 'loading' && <p className="admin-empty">Checking session…</p>}
        {auth.status === 'authenticated' && !auth.user.isAdmin && (
          <div className="admin-err" role="alert">
            Admin access required.
          </div>
        )}
        {isAdmin && (
          <>
            {renderStatus()}
            {group('Retirement Decision Journey', JOURNEY)}
            {group('Scheduler', SCHEDULER)}
            {group('Elsewhere', ELSEWHERE)}
          </>
        )}
      </main>
    </div>
  );
}
